'use client';
import React from 'react';
import { OrderTotals as Shared } from '@launchstore/shared-puck';
import { usePuckContext } from '@/components/puck/PuckContextProvider';
import { convertToLocale } from '@lib/util/money';
import { checkoutSelections } from './checkout-state';

const SharedRender = (Shared as any).render;

/**
 * Storefront OrderTotals — derives totals from the live cart OR order in
 * Puck context and formats with `convertToLocale`. On the cart/checkout
 * pages, `context.cart` is set. On order details/confirmation pages,
 * `context.order` is set. The wrapper prefers order over cart so the
 * same component works on both surfaces.
 *
 * For the checkout flow, the Puck context is SSR-frozen and doesn't
 * reflect client-side cart updates. The shipping total specifically
 * stays at 0 until the cart refetches after Continue. We use the
 * staging `checkoutSelections.shippingAmount` as a fallback so the
 * Order Summary on the checkout page shows the live shipping cost
 * the moment the user picks a radio.
 */
function OrderTotalsImpl(props: any) {
  const { context } = usePuckContext() as any;
  const src = context?.order ?? context?.cart;

  if (!src) {
    return <SharedRender {...props} totals={undefined} />;
  }

  const currency = src.currency_code || 'usd';
  const fmt = (amount: number) =>
    convertToLocale({ amount: Number(amount) || 0, currency_code: currency });

  // On the checkout page (src = cart), the Puck context is SSR-frozen.
  // cart.shipping_total stays at 0 until useCart refetches after the
  // user clicks Continue. Use the staging amount as a fallback so the
  // user sees the live shipping cost as soon as they pick a radio.
  // On order details/confirmation (src = order), shipping_total is
  // already correct from the order snapshot, no fallback needed.
  const isCart = !context?.order
  const cartShipping = Number(src.shipping_total) || 0
  const shipping = isCart
    ? (cartShipping || checkoutSelections.shippingAmount || 0)
    : cartShipping

  const subtotal = Number(src.subtotal) || 0
  const tax = Number(src.tax_total) || 0
  const discount = Number(src.discount_total) || 0

  // Gift card credit line — reduces the total, separate from discounts
  const creditLines = (src as any).credit_lines ?? [];
  const giftCardLine = creditLines.find(
    (cl: any) => cl.reference === 'gift_card' || cl.metadata?.type === 'gift_card'
  );
  const giftCardAmount = giftCardLine ? Number(giftCardLine.amount) || 0 : 0
  const giftCardCode = giftCardLine?.metadata?.code ?? giftCardLine?.reference_id

  // Volume savings — compare each line item's unit_price against its
  // variant's base price (from prices[] without min_quantity)
  let volumeSavingsAmount = 0
  if (isCart && Array.isArray(src.items)) {
    for (const item of src.items) {
      const variant = (item as any).variant
      const prices = variant?.prices ?? []
      const basePrice = prices.find((p: any) => !p.min_quantity || Number(p.min_quantity) <= 1)
      if (basePrice) {
        const base = Number(basePrice.amount) || 0
        const unit = Number(item.unit_price) || 0
        if (base > unit) {
          volumeSavingsAmount += (base - unit) * (item.quantity ?? 1)
        }
      }
    }
  }

  // Recompute the total locally so it stays in sync with the (possibly
  // staging-amount-overridden) shipping. The cart's `total` is the
  // Medusa-computed grand total; if we're overriding shipping, the
  // cart's total is stale too. Gift card credit reduces the total.
  const total = isCart
    ? (subtotal - discount - giftCardAmount + shipping + tax)
    : Number(src.total)

  const totals = {
    subtotal: fmt(subtotal),
    shipping: fmt(shipping),
    tax: fmt(tax),
    discount: discount > 0 ? `-${fmt(discount)}` : fmt(0),
    giftCard: giftCardAmount > 0 ? `-${fmt(giftCardAmount)}` : undefined,
    giftCardCode: giftCardAmount > 0 ? giftCardCode : undefined,
    volumeSavings: volumeSavingsAmount > 0 ? `-${fmt(volumeSavingsAmount)}` : undefined,
    total: fmt(Math.max(total, 0)),
    discountCode:
      Array.isArray(src.discounts) && src.discounts.length > 0
        ? src.discounts[0]?.code
        : undefined,
  };

  return <SharedRender {...props} totals={totals} />;
}

export const OrderTotals: any = {
  ...Shared,
  render: (props: any) => <OrderTotalsImpl {...props} />,
};
export default OrderTotals;
